/**
 * Servicio para manejar la información del usuario, incluyendo roles, grupos y clearances
 */
import { ENV } from '../config/env';

// Definición de los clearances en orden de restricción (de menor a mayor)
const CLEARANCE_LEVELS = {
  PUBLIC: 0,
  INTERNAL: 1,
  CONFIDENTIAL: 2,
  SECRET: 3,
  TOP_SECRET: 4
};

// Prefijos para categorizar los roles
const ROLE_PREFIXES = {
  ROLE: ENV.IM_CLAIM_ROLE_PREFIX,
  GROUP: ENV.IM_CLAIM_GROUPS_PREFIX,
  CLEARANCE: ENV.IM_CLAIM_CLEARANCE_PREFIX,
};

/**
 * Clase para manejar la información del usuario
 */
class UserService {
  constructor() {
    this.user = null;
    this.roles = [];
    this.groups = [];
    this.clearances = [];
    this.effectiveClearance = null;
  }

  /**
   * Inicializa el servicio con los datos del usuario del token OIDC
   * @param {Object} oidcUser - Usuario del contexto OIDC
   */
  initialize(oidcUser) {
    if (!oidcUser) {
      this.reset();
      return;
    }

    this.user = oidcUser;
    this.extractRolesAndGroups(oidcUser);
    this.determineEffectiveClearance();
  }

  /**
   * Extrae roles, grupos y clearances del token del usuario
   * @param {Object} oidcUser - Usuario del contexto OIDC
   */
  extractRolesAndGroups(oidcUser) {
    // Obtener roles del token (pueden estar en diferentes lugares según el proveedor)
    const roles = this.getRolesFromToken(oidcUser);

    this.roles = [];
    this.groups = [];
    this.clearances = [];

    roles.forEach(role => {
      if (role.startsWith(ROLE_PREFIXES.ROLE)) {
        // Es un rol - convertir a mayúsculas
        const roleName = role.substring(ROLE_PREFIXES.ROLE.length).toUpperCase();
        this.roles.push(roleName);
      } else if (role.startsWith(ROLE_PREFIXES.GROUP)) {
        // Es un grupo
        const groupName = role.substring(ROLE_PREFIXES.GROUP.length);
        this.groups.push(groupName);
      } else if (role.startsWith(ROLE_PREFIXES.CLEARANCE)) {
        // Es un clearance
        const clearanceName = role.substring(ROLE_PREFIXES.CLEARANCE.length);
        this.clearances.push(clearanceName);
      }
    });
  }

  /**
   * Obtiene los roles del token (compatible con diferentes proveedores OIDC)
   * @param {Object} oidcUser - Usuario del contexto OIDC
   * @returns {Array} Lista de roles
   */
  getRolesFromToken(oidcUser) {
    // Intentar obtener roles de diferentes lugares del token
    const profile = oidcUser.profile || {};
    const accessToken = oidcUser.access_token;

    // 1. Desde el profile (común en Keycloak)
    if (profile.realm_access && profile.realm_access.roles) {
      return profile.realm_access.roles;
    }

    // 2. Desde el access_token decodificado (si está disponible)
    if (accessToken) {
      try {
        // Decodificar el JWT (solo el payload, sin verificar la firma)
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (payload.resource_access && payload.resource_access.boveda && payload.resource_access.boveda.roles) {
          return payload.resource_access.boveda.roles;
        }
        if (payload.realm_access && payload.realm_access.roles) {
          return payload.realm_access.roles;
        }
      } catch (error) {
        console.warn('No se pudo decodificar el access token:', error);
      }
    }

    // 3. Desde el profile directamente
    if (profile.roles) {
      return profile.roles;
    }

    // 4. Fallback: buscar en otros campos del profile
    const allRoles = [];
    Object.keys(profile).forEach(key => {
      if (key.includes('role') || key.includes('group') || key.includes('clearance')) {
        if (Array.isArray(profile[key])) {
          allRoles.push(...profile[key]);
        }
      }
    });

    return allRoles;
  }

  /**
   * Determina el clearance efectivo (el más abarcativo)
   */
  determineEffectiveClearance() {
    if (this.clearances.length === 0) {
      this.effectiveClearance = 'PUBLIC';
      return;
    }

    // Encontrar el clearance con el nivel más alto
    let highestLevel = -1;
    let effectiveClearance = 'PUBLIC';

    this.clearances.forEach(clearance => {
      const level = CLEARANCE_LEVELS[clearance.toUpperCase()];
      if (level !== undefined && level > highestLevel) {
        highestLevel = level;
        effectiveClearance = clearance.toUpperCase();
      }
    });

    this.effectiveClearance = effectiveClearance;
  }

  /**
   * Verifica si el usuario tiene un rol específico
   * @param {string} role - Nombre del rol a verificar
   * @returns {boolean}
   */
  hasRole(role) {
    return this.roles.includes(role.toUpperCase());
  }

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   * @param {Array} roles - Lista de roles a verificar
   * @returns {boolean}
   */
  hasAnyRole(roles) {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Verifica si el usuario tiene un grupo específico
   * @param {string} group - Nombre del grupo a verificar
   * @returns {boolean}
   */
  hasGroup(group) {
    return this.groups.includes(group);
  }

  /**
   * Verifica si el usuario tiene alguno de los grupos especificados
   * @param {Array} groups - Lista de grupos a verificar
   * @returns {boolean}
   */
  hasAnyGroup(groups) {
    return groups.some(group => this.hasGroup(group));
  }

  /**
   * Verifica si el usuario tiene un clearance específico o superior
   * @param {string} requiredClearance - Clearance requerido
   * @returns {boolean}
   */
  hasClearance(requiredClearance) {
    const requiredLevel = CLEARANCE_LEVELS[requiredClearance.toUpperCase()];
    const userLevel = CLEARANCE_LEVELS[this.effectiveClearance];

    if (requiredLevel === undefined || userLevel === undefined) {
      return false;
    }

    return userLevel >= requiredLevel;
  }

  /**
   * Obtiene información completa del usuario
   * @returns {Object}
   */
  getUserInfo() {
    return {
      user: this.user,
      roles: [...this.roles],
      groups: [...this.groups],
      clearances: [...this.clearances],
      effectiveClearance: this.effectiveClearance,
      isAuthenticated: !!this.user
    };
  }

  /**
   * Obtiene solo los roles del usuario
   * @returns {Array}
   */
  getRoles() {
    return [...this.roles];
  }

  /**
   * Obtiene solo los grupos del usuario
   * @returns {Array}
   */
  getGroups() {
    return [...this.groups];
  }

  /**
   * Obtiene solo los clearances del usuario
   * @returns {Array}
   */
  getClearances() {
    return [...this.clearances];
  }

  /**
   * Obtiene el clearance efectivo del usuario
   * @returns {string}
   */
  getEffectiveClearance() {
    return this.effectiveClearance;
  }

  /**
   * Resetea el servicio
   */
  reset() {
    this.user = null;
    this.roles = [];
    this.groups = [];
    this.clearances = [];
    this.effectiveClearance = null;
  }

  /**
   * Obtiene el nivel numérico de un clearance
   * @param {string} clearance - Nombre del clearance
   * @returns {number}
   */
  getClearanceLevel(clearance) {
    return CLEARANCE_LEVELS[clearance.toUpperCase()] || 0;
  }

  /**
   * Obtiene todos los clearances disponibles ordenados por nivel
   * @returns {Array}
   */
  getAllClearances() {
    return Object.keys(CLEARANCE_LEVELS).sort((a, b) => CLEARANCE_LEVELS[a] - CLEARANCE_LEVELS[b]);
  }
}

// Crear una instancia singleton del servicio
const userService = new UserService();

export default userService;
export { CLEARANCE_LEVELS, ROLE_PREFIXES };
